import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { PermissionCode } from "../../permissions/permissions.js";
import {
  AI_CATEGORY_LABELS,
  AI_REVIEW_STATUS_LABELS,
  AI_REVIEW_SUBJECT_LABELS,
  AI_SEVERITY_LABELS,
  type AIReportReview,
  type AIReviewSubjectType
} from "../../domain/ai-review/ai-review-model.js";
import { KNOWLEDGE_SOURCE_LABELS, SUGGESTED_KNOWLEDGE_DOCUMENTS, type KnowledgeEntry, type KnowledgeSourceType } from "../../domain/ai-review/technical-knowledge.js";
import type { AISettings } from "../../domain/ai-review/ai-settings.js";
import type { AIPendingItem, AIReviewOverview } from "../../application/services/inspect-ia-review-service.js";
import type { ReviewableItem } from "../../application/services/ai-review-adapters.js";
import "../components/ai-review-panel.js";
import "../components/ai-assistant-drawer.js";

type Tab = "OVERVIEW" | "REVIEW" | "ASSISTANT" | "PENDING" | "KNOWLEDGE" | "HISTORY" | "SETTINGS";

const esc = (v: unknown): string => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const errorText = (e: unknown, fallback: string): string => (e as { safeMessage?: string })?.safeMessage || (e as Error)?.message || fallback;
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

/**
 * INSPECT IA REVIEW — IA oficial do INSPECT IA (substitui o Córtex).
 *
 *   01 VISÃO GERAL · 02 REVISÃO DE RELATÓRIOS · 03 ASSISTENTE TÉCNICO ·
 *   04 PENDÊNCIAS DE REVISÃO · 05 BASE TÉCNICA · 06 HISTÓRICO · 07 CONFIGURAÇÕES IA
 *
 * Toda leitura e gravação passa pela fachada `inspectIaReviewService`, que
 * confere a permissão de cada ato. Esconder aba aqui é só desenho.
 */
export class InspectIaReviewPage extends HTMLElement {
  private tab: Tab = "OVERVIEW";
  private error: string | null = null;
  private notice: string | null = null;
  private overview: AIReviewOverview | null = null;
  private types: Array<{ type: AIReviewSubjectType; label: string }> = [];
  private selectedType: AIReviewSubjectType | "" = "";
  private reviewable: ReviewableItem[] = [];
  private selectedReport = "";
  private pending: AIPendingItem[] = [];
  private history: AIReportReview[] = [];
  private knowledge: KnowledgeEntry[] = [];
  private settings: (AISettings & { providerLabels: Record<string, string> }) | null = null;
  private kbForm: Partial<KnowledgeEntry> & { scope?: "GLOBAL" | "CLIENT" } = { sourceType: "NORM", scope: "CLIENT" };

  connectedCallback(): void {
    this.addEventListener("click", e => this.onClick(e));
    this.addEventListener("change", e => this.onChange(e));
    this.addEventListener("input", e => this.onChange(e));
    this.addEventListener("ai-review-applied", () => { this.notice = "Alteração aplicada ao relatório. Execute nova revisão antes de enviar para análise."; });
    const q = new URLSearchParams(location.search);
    if (q.get("type")) { this.selectedType = q.get("type") as AIReviewSubjectType; this.tab = "REVIEW"; }
    if (q.get("id")) this.selectedReport = q.get("id") || "";
    void this.loadTab();
  }

  private get svc(): any { return (window as any).app.platform.inspectIaReviewService; }
  private can(p: string): boolean { return contextStore.hasPermission(p); }

  private async loadTab(): Promise<void> {
    this.error = null;
    this.render();
    const ctx = sessionRequestContext();
    try {
      if (this.tab === "OVERVIEW") {
        this.overview = await this.svc.overview(ctx);
      }
      if (this.tab === "REVIEW" || this.tab === "ASSISTANT") {
        const supported = await this.svc.supportedTypes(ctx);
        if (!Array.isArray(supported)) {
          this.types = [];
          console.warn("[INSPECT-IA-REVIEW] supportedTypes did not return an array:", supported);
        } else {
          this.types = supported;
        }
        if (this.selectedType) {
          const list = await this.svc.listReviewable(ctx, this.selectedType);
          this.reviewable = Array.isArray(list) ? list : [];
        }
      }
      if (this.tab === "PENDING") {
        const pList = await this.svc.listPending(ctx);
        this.pending = Array.isArray(pList) ? pList : [];
      }
      if (this.tab === "HISTORY") {
        const hList = await this.svc.listHistory(ctx);
        this.history = Array.isArray(hList) ? hList : [];
      }
      if (this.tab === "KNOWLEDGE") {
        const kList = await this.svc.listKnowledge(ctx);
        this.knowledge = Array.isArray(kList) ? kList : [];
      }
      if (this.tab === "SETTINGS") {
        this.settings = await this.svc.getSettings(ctx);
      }
    } catch (e) {
      // Leitura negada NÃO é "nenhum registro": a tela diz o motivo.
      this.error = errorText(e, "Não foi possível carregar os dados do INSPECT IA REVIEW.");
    }
    this.render();
  }

  private onChange(e: Event): void {
    const t = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (t.id === "air-type" && e.type === "change") {
      this.selectedType = t.value as AIReviewSubjectType;
      this.selectedReport = "";
      void this.loadTab();
    } else if (t.id === "air-report" && e.type === "change") {
      this.selectedReport = t.value;
      this.render();
    } else if (t.dataset["kb"]) {
      const k = t.dataset["kb"] as keyof typeof this.kbForm;
      (this.kbForm as Record<string, unknown>)[k] = k === "tags" ? t.value.split(",").map(s => s.trim()).filter(Boolean) : t.value;
    }
  }

  private async onClick(e: Event): Promise<void> {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-pg]");
    if (!el) return;
    const a = el.dataset["pg"];
    const ctx = sessionRequestContext();
    try {
      if (a === "tab") { this.tab = el.dataset["tab"] as Tab; this.notice = null; await this.loadTab(); return; }
      if (a === "open" || a === "open-history") {
        this.selectedType = el.dataset["type"] as AIReviewSubjectType;
        this.selectedReport = el.dataset["id"] || "";
        this.tab = "REVIEW";
        await this.loadTab();
        return;
      }
      if (a === "assistant") {
        const drawer = this.querySelector("ai-assistant-drawer") as (HTMLElement & { open: (o: object) => void }) | null;
        const withReport = el.dataset["ctx"] === "report" && this.selectedType && this.selectedReport;
        if (drawer && !withReport) { delete drawer.dataset["type"]; delete drawer.dataset["id"]; }
        drawer?.open(withReport ? { title: this.reviewable.find(r => r.reportId === this.selectedReport)?.label || "Relatório", type: this.selectedType, id: this.selectedReport } : { title: "Base Técnica" });
        return;
      }
      if (a === "kb-save") {
        await this.svc.saveKnowledgeEntry(ctx, this.kbForm);
        this.kbForm = { sourceType: this.kbForm.sourceType ?? "NORM", scope: this.kbForm.scope ?? "CLIENT" };
        this.notice = "Trecho cadastrado na Base Técnica.";
        await this.loadTab();
        return;
      }
      if (a === "kb-inactivate") {
        if (!window.confirm("Inativar este trecho? Ele deixa de ser usado pelo assistente (não é apagado).")) return;
        await this.svc.inactivateKnowledgeEntry(ctx, el.dataset["id"]);
        await this.loadTab();
        return;
      }
      if (a === "settings-save" && this.settings) {
        const form = this.querySelector("#air-settings") as HTMLFormElement;
        const v = (n: string) => (form.elements.namedItem(n) as HTMLInputElement | HTMLSelectElement | null);
        const input: Partial<AISettings> = {
          provider: v("provider")!.value as AISettings["provider"], model: v("model")!.value, endpoint: v("endpoint")!.value,
          maxTokens: Number(v("maxTokens")!.value), timeoutMs: Number(v("timeoutMs")!.value), retries: Number(v("retries")!.value), temperature: Number(v("temperature")!.value),
          knowledgeBaseEnabled: (v("knowledgeBaseEnabled") as HTMLInputElement).checked, autoApplySafeLanguage: (v("autoApplySafeLanguage") as HTMLInputElement).checked,
          requireReviewBeforeSubmit: false, offlinePolicy: "ALLOW_WITH_RECORD",
          logging: v("logging")!.value as AISettings["logging"]
        };
        await this.svc.updateSettings(ctx, input, this.settings.version);
        this.notice = "Configurações do INSPECT IA REVIEW salvas.";
        await this.loadTab();
      }
    } catch (err) {
      this.error = errorText(err, "A operação não pôde ser concluída.");
      this.render();
    }
  }

  render(): void {
    const tabs: Array<[Tab, string, boolean]> = [
      ["OVERVIEW", "01 VISÃO GERAL", true],
      ["REVIEW", "02 REVISÃO DE RELATÓRIOS", true],
      ["ASSISTANT", "03 ASSISTENTE TÉCNICO", this.can(PermissionCode.AiAssistantUse)],
      ["PENDING", "04 PENDÊNCIAS DE REVISÃO", true],
      ["KNOWLEDGE", "05 BASE TÉCNICA", this.can(PermissionCode.AiAssistantUse) || this.can(PermissionCode.AiSettingsRead)],
      ["HISTORY", "06 HISTÓRICO", this.can(PermissionCode.AiReviewHistory)],
      ["SETTINGS", "07 CONFIGURAÇÕES IA", this.can(PermissionCode.AiSettingsRead)]
    ];
    this.innerHTML = `
      <style>${STYLES}</style>
      <div class="pg">
        <header class="pg-head">
          <h1>INSPECT IA REVIEW</h1>
          <p>Inteligência artificial oficial do INSPECT IA: revisa relatórios, corrige português, identifica inconsistências, apoia tecnicamente e consulta a Base Técnica. <strong>A IA identifica e auxilia; o profissional decide</strong> — ela não aprova, não altera números, não inventa norma nem resultado e não substitui inspetor nem PLH.</p>
        </header>
        <nav class="pg-tabs">${tabs.filter(t => t[2]).map(([t, l]) => `<button class="pg-tab ${this.tab === t ? "on" : ""}" data-pg="tab" data-tab="${t}">${esc(l)}</button>`).join("")}</nav>
        ${this.notice ? `<div class="pg-notice">${esc(this.notice)}</div>` : ""}
        ${this.error ? `<div class="pg-error">${esc(this.error)}</div>` : ""}
        ${this.body()}
      </div>
      <ai-assistant-drawer></ai-assistant-drawer>`;
  }

  private body(): string {
    switch (this.tab) {
      case "OVERVIEW": return this.overviewHtml();
      case "REVIEW": return this.reviewHtml();
      case "ASSISTANT": return this.assistantHtml();
      case "PENDING": return this.pendingHtml();
      case "KNOWLEDGE": return this.knowledgeHtml();
      case "HISTORY": return this.historyHtml();
      case "SETTINGS": return this.settingsHtml();
    }
  }

  private overviewHtml(): string {
    const o = this.overview;
    if (!o) return this.error ? "" : `<p class="pg-muted">Carregando…</p>`;
    const card = (label: string, n: number, tone = "") => `<div class="pg-card ${tone}"><div class="pg-card-n">${n}</div><div class="pg-card-l">${esc(label)}</div></div>`;
    return `
      <div class="pg-cards">
        ${card("RELATÓRIOS AGUARDANDO REVISÃO IA", o.awaitingReview, o.awaitingReview ? "warn" : "")}
        ${card("RELATÓRIOS REVISADOS", o.reviewed)}
        ${card("CORREÇÕES DE PORTUGUÊS", o.languageCorrections)}
        ${card("ALERTAS TÉCNICOS", o.technicalAlerts, o.technicalAlerts ? "warn" : "")}
        ${card("INCONSISTÊNCIAS CRÍTICAS", o.criticalInconsistencies, o.criticalInconsistencies ? "bad" : "")}
        ${card("PENDÊNCIAS NÃO RESOLVIDAS", o.unresolved, o.unresolved ? "warn" : "")}
      </div>
      <p class="pg-muted">Provedor: ${esc(o.provider.label)} — ${o.provider.available ? "disponível" : "INDISPONÍVEL"}. Contagens da revisão vigente de cada documento${contextStore.get().activeClientId ? " do Cliente ativo" : ""}.</p>`;
  }

  private reviewHtml(): string {
    return `
      <div class="pg-filters">
        <label>Tipo de documento<select id="air-type"><option value="">Selecione…</option>${this.types.map(t => `<option value="${t.type}" ${this.selectedType === t.type ? "selected" : ""}>${esc(t.label)}</option>`).join("")}</select></label>
        <label>Documento<select id="air-report" ${this.selectedType ? "" : "disabled"}><option value="">${this.selectedType ? (this.reviewable.length ? "Selecione…" : "Nenhum documento deste tipo") : "Escolha o tipo"}</option>${this.reviewable.map(r => `<option value="${esc(r.reportId)}" ${this.selectedReport === r.reportId ? "selected" : ""}>${esc(r.label || r.reportId)}${r.status ? ` · ${esc(r.status)}` : ""}</option>`).join("")}</select></label>
        ${this.selectedType && this.selectedReport && this.can(PermissionCode.AiAssistantUse) ? `<button class="pg-btn" data-pg="assistant" data-ctx="report">ASSISTENTE TÉCNICO</button>` : ""}
      </div>
      ${this.selectedType && this.selectedReport ? `<ai-review-panel data-type="${esc(this.selectedType)}" data-id="${esc(this.selectedReport)}"></ai-review-panel>` : `<p class="pg-muted">Escolha um documento. A revisão também fica disponível dentro de cada relatório (ME, LP, PM, US, B-Scan, AVR, RDO e RI).</p>`}`;
  }

  private assistantHtml(): string {
    return `
      <div class="pg-box">
        <h2>ASSISTENTE TÉCNICO INSPECT IA</h2>
        <p>Dentro de um relatório, o assistente recebe sozinho o contexto (Cliente, Projeto, TAG, resultados, normas, pendências). Aqui, sem relatório aberto, ele consulta só a Base Técnica.</p>
        <p class="pg-muted">Toda afirmação normativa precisa de fonte na Base Técnica (documento, revisão, seção). Sem suporte, a resposta é: "Não encontrei suporte suficiente na base técnica configurada para confirmar esta afirmação."</p>
        <div class="pg-actions"><button class="pg-btn pg-primary" data-pg="assistant">ABRIR ASSISTENTE</button>${this.selectedType && this.selectedReport ? `<button class="pg-btn" data-pg="assistant" data-ctx="report">ABRIR COM O RELATÓRIO SELECIONADO</button>` : ""}</div>
      </div>`;
  }

  private pendingHtml(): string {
    if (this.pending.length === 0) return this.error ? "" : `<p class="pg-muted">Nenhuma pendência de revisão IA.</p>`;
    return `<div class="pg-table-wrap"><table class="pg-table"><thead><tr><th>DOCUMENTO</th><th>NÍVEL</th><th>ÁREA</th><th>CAMPO</th><th>MOTIVO</th><th></th></tr></thead><tbody>${this.pending.map(p => `
      <tr><td>${esc(AI_REVIEW_SUBJECT_LABELS[p.review.subjectType])}<span class="pg-muted">${esc(p.review.reportNumber || p.review.reportId)}</span></td>
      <td class="sev-${p.finding.severity}">${esc(AI_SEVERITY_LABELS[p.finding.severity])}</td><td>${esc(AI_CATEGORY_LABELS[p.finding.category])}</td><td>${esc(p.finding.fieldLabel)}</td><td>${esc(p.finding.explanation)}</td>
      <td><button class="pg-btn sm" data-pg="open" data-type="${esc(p.review.subjectType)}" data-id="${esc(p.review.reportId)}">ABRIR</button></td></tr>`).join("")}</tbody></table></div>`;
  }

  private knowledgeHtml(): string {
    const canEdit = this.can(PermissionCode.AiSettingsUpdate);
    const f = this.kbForm;
    const sources = Object.entries(KNOWLEDGE_SOURCE_LABELS) as Array<[KnowledgeSourceType, string]>;
    return `
      <p class="pg-muted">Base técnica CONTROLADA: trechos de documentos (normas, procedimentos, especificações) cadastrados com documento, revisão e seção. O assistente só afirma o que encontra aqui. A base começa vazia — nenhum texto normativo é embutido no sistema. Prioridade: documentos do projeto → procedimento → norma → especificação do cliente → base geral.</p>
      ${canEdit ? `
      <div class="pg-box">
        <h2>CADASTRAR TRECHO</h2>
        <div class="pg-grid">
          <label>Tipo de fonte<select data-kb="sourceType">${sources.map(([k, l]) => `<option value="${k}" ${f.sourceType === k ? "selected" : ""}>${esc(l)}</option>`).join("")}</select></label>
          <label>Escopo<select data-kb="scope"><option value="CLIENT" ${f.scope !== "GLOBAL" ? "selected" : ""}>Cliente ativo</option><option value="GLOBAL" ${f.scope === "GLOBAL" ? "selected" : ""}>Todos os Clientes (base geral)</option></select></label>
          <label>Documento<input data-kb="documentCode" list="kb-docs" value="${esc(f.documentCode ?? "")}" placeholder="API 570, PR-ME-01…"><datalist id="kb-docs">${SUGGESTED_KNOWLEDGE_DOCUMENTS.map(d => `<option value="${esc(d)}">`).join("")}</datalist></label>
          <label>Título<input data-kb="documentTitle" value="${esc(f.documentTitle ?? "")}"></label>
          <label>Revisão<input data-kb="revision" value="${esc(f.revision ?? "")}"></label>
          <label>Seção / item<input data-kb="section" value="${esc(f.section ?? "")}"></label>
          <label>Projeto (id, só para documento do projeto)<input data-kb="projectId" value="${esc(f.projectId ?? "")}"></label>
          <label>Palavras-chave (separadas por vírgula)<input data-kb="tags" value="${esc((f.tags ?? []).join(", "))}"></label>
          <label class="pg-wide">Trecho do documento<textarea data-kb="text" rows="4">${esc(f.text ?? "")}</textarea></label>
        </div>
        <div class="pg-actions"><button class="pg-btn pg-primary" data-pg="kb-save">SALVAR TRECHO</button></div>
      </div>` : ""}
      ${this.knowledge.length === 0 ? `<p class="pg-muted">Nenhum trecho cadastrado.</p>` : `
      <div class="pg-table-wrap"><table class="pg-table"><thead><tr><th>DOCUMENTO</th><th>REVISÃO</th><th>SEÇÃO</th><th>FONTE</th><th>ESCOPO</th><th>TRECHO</th><th>STATUS</th><th></th></tr></thead><tbody>${this.knowledge.map(k => `
        <tr><td><strong>${esc(k.documentCode)}</strong><span class="pg-muted">${esc(k.documentTitle)}</span></td><td>${esc(k.revision)}</td><td>${esc(k.section)}</td><td>${esc(KNOWLEDGE_SOURCE_LABELS[k.sourceType])}</td>
        <td>${k.clientId ? "Cliente" : "Geral"}</td><td class="pg-excerpt">${esc(k.text.slice(0, 180))}${k.text.length > 180 ? "…" : ""}</td><td>${k.status === "ACTIVE" ? "ATIVO" : "INATIVO"}</td>
        <td>${canEdit && k.status === "ACTIVE" ? `<button class="pg-btn sm" data-pg="kb-inactivate" data-id="${esc(k.id)}">INATIVAR</button>` : ""}</td></tr>`).join("")}</tbody></table></div>`}`;
  }

  private historyHtml(): string {
    if (this.history.length === 0) return this.error ? "" : `<p class="pg-muted">Nenhuma revisão IA registrada.</p>`;
    return `<div class="pg-table-wrap"><table class="pg-table"><thead><tr><th>RELATÓRIO</th><th>REVISÃO</th><th>DATA</th><th>RESULTADO</th><th>CORREÇÕES</th><th>ALERTAS</th><th>CRÍTICOS</th><th>RESPONSÁVEL</th><th>MODELO</th><th></th></tr></thead><tbody>${this.history.map(r => `
      <tr><td>${esc(AI_REVIEW_SUBJECT_LABELS[r.subjectType])}<span class="pg-muted">${esc(r.reportNumber || r.reportId)}</span></td><td>${esc(r.reportRevision || "—")}</td><td>${esc(fmt(r.startedAt))}</td>
      <td>${esc(AI_REVIEW_STATUS_LABELS[r.status])}</td><td>${r.autoAppliedCount}</td><td>${r.warningCount}</td><td>${r.criticalCount}</td><td>${esc(r.requestedByName || r.requestedBy)}</td>
      <td class="pg-muted">${esc(r.modelProvider)} ${esc(r.modelVersion)} · ${esc(r.promptVersion)}</td>
      <td><button type="button" class="pg-btn sm" data-pg="open-history" data-review-id="${esc(r.id)}" data-type="${esc(r.subjectType)}" data-id="${esc(r.reportId)}">ABRIR</button></td></tr>`).join("")}</tbody></table></div>`;
  }

  private settingsHtml(): string {
    const s = this.settings;
    if (!s) return this.error ? "" : `<p class="pg-muted">Carregando…</p>`;
    const d = this.can(PermissionCode.AiSettingsUpdate) ? "" : "disabled";
    const check = (name: keyof AISettings, label: string, hint: string) => `<label class="pg-check"><input type="checkbox" name="${name}" ${s[name] ? "checked" : ""} ${d}> ${esc(label)}<span class="pg-muted">${esc(hint)}</span></label>`;
    return `
      <form id="air-settings" class="pg-box" onsubmit="return false">
        <h2>CONFIGURAÇÕES — INSPECT IA REVIEW <span class="pg-muted">versão ${s.version}${s.updatedAt ? ` · ${esc(fmt(s.updatedAt))}` : ""}</span></h2>

        <div style="background: var(--ia-bg-inset, #15181B); border: 1px solid var(--ia-line, #3A3F45); border-radius: 6px; padding: 12px 16px; margin: 12px 0;">
          <div style="font-weight: 700; font-size: 0.8rem; color: var(--ia-primary, #C8A96A); margin-bottom: 4px;">MODO OPERACIONAL: CONSULTIVO [fixo]</div>
          <div style="font-size: 0.75rem; color: var(--ia-fg-soft, #AEAEAE); line-height: 1.4;">
            A IA sinaliza inconsistências e recomenda revisões. A decisão e o prosseguimento pertencem exclusivamente ao profissional responsável. A IA nunca bloqueia envios ou assinaturas.
          </div>
        </div>

        <div class="pg-grid">
          <label>Provedor<select name="provider" ${d}>${Object.entries(s.providerLabels).map(([k, l]) => `<option value="${k}" ${s.provider === k ? "selected" : ""}>${esc(l)}</option>`).join("")}</select></label>
          <label>Modelo<input name="model" value="${esc(s.model)}" ${d}></label>
          <label>Endpoint (provedor externo)<input name="endpoint" value="${esc(s.endpoint)}" ${d} placeholder="não integrado nesta entrega"></label>
          <label>Limite de tokens<input name="maxTokens" type="number" value="${s.maxTokens}" ${d}></label>
          <label>Timeout (ms)<input name="timeoutMs" type="number" value="${s.timeoutMs}" ${d}></label>
          <label>Tentativas (resposta inválida)<input name="retries" type="number" min="0" max="3" value="${s.retries}" ${d}></label>
          <label>Temperature<input name="temperature" type="number" step="0.1" min="0" max="1" value="${s.temperature}" ${d}></label>
          <label>Logging<select name="logging" ${d}><option value="METADATA_ONLY" ${s.logging === "METADATA_ONLY" ? "selected" : ""}>Só metadados (nunca o conteúdo)</option><option value="OFF" ${s.logging === "OFF" ? "selected" : ""}>Desligado</option></select></label>
        </div>
        ${check("knowledgeBaseEnabled", "Base Técnica ativa", " — o assistente consulta os trechos cadastrados.")}
        ${check("autoApplySafeLanguage", "Correção linguística automática", " — só correções classificadas como seguras, com antes/depois guardados.")}
        <p class="pg-muted">Chaves de API e credenciais nunca são aceitas aqui nem no navegador: ficam só no cofre do servidor. Nenhum provedor externo está integrado nesta entrega — escolhê-lo deixa a revisão INDISPONÍVEL (nunca cai calado no motor local).</p>
        ${d ? `<p class="pg-muted">Somente leitura (sem ai_settings.update).</p>` : `<div class="pg-actions"><button type="button" class="pg-btn pg-primary" data-pg="settings-save">SALVAR CONFIGURAÇÕES</button></div>`}
      </form>`;
  }
}

const STYLES = `
  .pg { padding:20px; color: var(--ia-fg, #E5E7EB); }
  .pg-head h1 { margin:0; font-size:1.2rem; letter-spacing:0.04em; }
  .pg-head p { font-size:0.78rem; color: var(--ia-fg-soft, #AEAEAE); max-width:980px; line-height:1.5; }
  .pg-tabs { display:flex; gap:6px; flex-wrap:wrap; margin:12px 0; }
  .pg-tab { background: var(--ia-bg-panel, #1F2328); color: var(--ia-fg-soft, #AEAEAE); border:1px solid var(--ia-line, #3A3F45); padding:6px 12px; border-radius:6px; font-size:0.7rem; font-weight:700; cursor:pointer; }
  .pg-tab.on { color: var(--ia-fg, #fff); border-color: var(--ia-primary, #C8A96A); }
  .pg-cards { display:grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap:10px; }
  .pg-card { border:1px solid var(--ia-line, #3A3F45); border-radius:8px; padding:14px; background: var(--ia-bg-panel, #1F2328); }
  .pg-card.warn { border-color: rgba(245,158,11,0.5); } .pg-card.bad { border-color: rgba(239,68,68,0.6); }
  .pg-card-n { font-size:1.6rem; font-weight:800; } .pg-card-l { font-size:0.66rem; letter-spacing:0.05em; color: var(--ia-fg-soft, #AEAEAE); margin-top:4px; }
  .pg-muted { font-size:0.72rem; color: var(--ia-fg-muted, #9CA3AF); display:block; }
  .pg-filters, .pg-grid { display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; }
  .pg-grid { display:grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
  .pg-filters label, .pg-grid label { display:flex; flex-direction:column; gap:4px; font-size:0.66rem; letter-spacing:0.03em; text-transform:uppercase; color: var(--ia-fg-soft, #AEAEAE); min-width:220px; }
  .pg-filters select, .pg-grid input, .pg-grid select, .pg-grid textarea { background: var(--ia-bg-inset, #15181B); color: var(--ia-fg, #E5E7EB); border:1px solid var(--ia-line, #3A3F45); border-radius:5px; padding:6px 8px; font-size:0.8rem; text-transform:none; font-family:inherit; }
  .pg-wide { grid-column: 1 / -1; }
  .pg-check { display:block; font-size:0.78rem; margin:8px 0; }
  .pg-box { border:1px solid var(--ia-line, #3A3F45); border-radius:8px; padding:14px; background: var(--ia-bg-panel, #1F2328); margin:12px 0; }
  .pg-box h2 { font-size:0.85rem; margin:0 0 10px; letter-spacing:0.04em; }
  .pg-box p { font-size:0.78rem; line-height:1.5; }
  .pg-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:10px; }
  .pg-btn { background: var(--ia-bg-raised, #2A2F35); color: var(--ia-fg, #E5E7EB); border:1px solid var(--ia-line, #3A3F45); padding:7px 12px; border-radius:6px; font-size:0.7rem; font-weight:700; cursor:pointer; }
  .pg-btn.sm { padding:4px 8px; font-size:0.64rem; }
  .pg-primary { background: var(--ia-action-bg, #3C3C3C); color: var(--ia-action-fg, #fff); border-color: transparent; }
  .pg-table-wrap { overflow-x:auto; }
  .pg-table { width:100%; border-collapse:collapse; font-size:0.74rem; }
  .pg-table th { text-align:left; padding:7px 8px; background: var(--color-primary, #3C3C3C); color: var(--ia-on-color, #fff); font-size:0.64rem; letter-spacing:0.04em; white-space:nowrap; }
  .pg-table td { padding:7px 8px; border-bottom:1px solid var(--ia-line, #3A3F45); vertical-align:top; }
  .pg-excerpt { max-width:360px; }
  .sev-CRITICO { color: var(--ia-danger-fg, #fca5a5); font-weight:800; } .sev-TECNICO { color: var(--ia-warning-fg, #fbbf24); font-weight:700; }
  .pg-error { border:1px solid rgba(239,68,68,0.45); background: rgba(239,68,68,0.10); color: var(--ia-danger-fg, #fca5a5); border-radius:6px; padding:10px 12px; margin:8px 0; font-size:0.78rem; }
  .pg-notice { border:1px solid var(--ia-line, #3A3F45); background: var(--ia-bg-panel, #1F2328); border-radius:6px; padding:10px 12px; margin:8px 0; font-size:0.78rem; }
  @media (max-width: 640px) { .pg { padding:16px; } .pg-filters label, .pg-grid label { min-width:0; } }
`;

if (!customElements.get("app-inspect-ia-review-page")) customElements.define("app-inspect-ia-review-page", InspectIaReviewPage);
